<template>
  <div :class="{ 'force-show-placeholder': currentActions.length }">
    <popper
      ref="popperRef"
      class="action-popper"
      trigger="click"
      v-on:show="initialize()"
      :options="poperOptions"
    >
      <div class="popper">
        <div class="p-3" v-show="isCurrentStatus(FILTER_STATUSES.CHOOSE_ACTION)">
          <div class="row mt-0">
            <div class="col">
              <h6 class="header-pretitle mb--4 mt-3 text-left">Behavior filters</h6>
            </div>
            <div class="col text-right">
              <button @click="reset()" class="btn btn-outline-secondary btn-sm mb--2">Reset</button>
            </div>
          </div>
          <hr class="mb-4" />
          <div
            v-for="(currentAction, currentActionIndex) in allCurrentActions"
            :key="currentAction.id"
          >
            <CustomerActionForm
              :actionIndex="currentActionIndex"
              :action="currentAction"
              :activeActionLabel="getActionLabel(currentAction, true)"
              @updateAction="updateAction"
              @removeAction="removeAction"
            />
            <div
              class="d-flex align-items-center my-3"
              v-if="currentActionIndex !== allCurrentActions.length - 1"
            >
              <hr class="w-100" />
              <span class="badge badge-soft-primary">AND</span>
              <hr class="w-100" />
            </div>
          </div>
          <hr class="w-100 mt-4" />
          <div class="text-left mt-4 mb-2">
            <a
              class="btn btn-sm btn-outline-secondary btn-rounded-circle"
              href="javascript:;"
              @click="addNewAction"
            >
              <span class="fe fe-plus text-black"></span>
            </a>
            <span class="text-muted ml-3">Add behavior</span>
          </div>
        </div>
        <CustomerActionFilterItemList
          type="campaign"
          :items="campaigns"
          @input="selectCampaign"
          v-show="isCurrentStatus(FILTER_STATUSES.CHOOSE_CAMPAIGN)"
        />
        <CustomerActionFilterItemList
          type="source"
          :items="sources"
          @input="selectSource"
          v-show="isCurrentStatus(FILTER_STATUSES.CHOOSE_SOURCE)"
        />
        <CustomerActionFilterItemList
          type="offer"
          :items="promos"
          @input="selectPromo"
          v-show="isCurrentStatus(FILTER_STATUSES.CHOOSE_PROMO)"
        />
      </div>

      <multiselect
        slot="reference"
        :style="{
          width: currentActions && currentActions.length ? 'auto' : '122px'
        }"
        selectLabel
        :hideSelected="false"
        :close-on-select="false"
        :multiple="true"
        :searchable="false"
        :showLabels="false"
        :limit="1"
        :limitText="(count) => `+${count} more`"
        :options="[]"
        :custom-label="getActionLabel"
        v-model="currentActions"
        @input="applyFilters"
        track-by="id"
      >
        <template slot="placeholder">
          <span class="text-secondary"> <i class="fe fe-target"></i> Any behavior </span>
        </template>
      </multiselect>
    </popper>
  </div>
</template>

<script>
import { map } from 'lodash-es'

import { createNamespacedHelpers } from 'vuex'

import Multiselect from 'vue-multiselect'
import Popper from 'vue-popperjs'
import CustomerActionFilterItemList from './CustomerActionFilterItemList'
import CustomerActionForm from './CustomerActionForm'

const CampaignModule = createNamespacedHelpers('campaign')
const CustomerModule = createNamespacedHelpers('customer')
const SurveyModule = createNamespacedHelpers('survey')
const PromoModule = createNamespacedHelpers('promo')
const ActionFilterModule = createNamespacedHelpers('actionFilter')

export default {
  name: 'CustomerActionFilter',
  components: {
    CustomerActionFilterItemList,
    CustomerActionForm,
    Multiselect,
    Popper
  },
  props: ['campaignSource'],
  data: () => ({
    status: '',
    selectedActionIndex: -1,
    currentActions: [],
    allCurrentActions: [], // same currentActions but Holds 1st default action when no action is selected
    poperOptions: {
      placement: 'bottom',
      modifiers: {
        offset: { offset: '0,6px' },
        flip: { enabled: false },
        gpuAcceleration: true
      }
    }
  }),
  computed: {
    ...CampaignModule.mapState(['campaigns']),
    ...CustomerModule.mapState(['listFilters']),
    ...SurveyModule.mapState(['SURVEY_SOURCES']),
    ...PromoModule.mapState(['promos']),
    ...ActionFilterModule.mapState([
      'ANALYTIC_EVENT_TYPES',
      'ANALYTIC_EVENTS',
      'FILTER_KEYS',
      'FILTER_STATUSES'
    ]),
    sources() {
      return map(this.SURVEY_SOURCES, (title, _id) => ({
        _id,
        title
      }))
    }
  },
  methods: {
    ...CampaignModule.mapActions(['queryCampaigns']),
    ...PromoModule.mapActions(['queryPromos', 'queryPromosWithSource']),
    getActionLabel(action, isShortLabel) {
      if (action.eventType == this.ANALYTIC_EVENT_TYPES.CHOOSE_ACTION) {
        return 'Choose action'
      }
      let label = ''
      const eventType = this.getAnalyticEventType(action.eventType)
      const campaign = this.getCampaignById(action.campaignId)
      const sourceTitle = this.SURVEY_SOURCES[action.source]
      const promo = this.getPromoById(action.promoId)
      switch (eventType.type) {
        case this.ANALYTIC_EVENT_TYPES.SENT_CAMPAIGN:
          label = `${campaign ? campaign.title.toLowerCase() : 'any campaign'}`
          break
        case this.ANALYTIC_EVENT_TYPES.COME_FROM:
          label = `${isShortLabel ? 'C' : 'c'}ome from ${
            sourceTitle ? sourceTitle.toLowerCase() : 'any source'
          }`
          break
        case this.ANALYTIC_EVENT_TYPES.REDEEM_PROMO:
          label = `${
            promo ? promo.nickname.toLowerCase() || promo.title.toLowerCase() : 'any offer'
          }`
          break
        default:
          label = isShortLabel ? eventType.name : `${eventType.name.toLowerCase()}`
      }
      return `${this.getLabelPrefix(action, eventType, isShortLabel)}${label}`
    },
    getLabelPrefix(action, eventType, isShortLabel) {
      const prefix = action.did ? 'Did ' : 'Did not '
      switch (eventType.type) {
        case this.ANALYTIC_EVENT_TYPES.SENT_CAMPAIGN:
          return isShortLabel ? 'Receive ' : `${action.did ? 'Received ' : `${prefix}receive `}`
        case this.ANALYTIC_EVENT_TYPES.REDEEM_PROMO:
          return isShortLabel ? 'Redeem ' : `${action.did ? 'Redeemed ' : `${prefix}redeem `}`
        default:
          if (isShortLabel) return ''
          return prefix
      }
    },
    getCampaignById(id) {
      return this.campaigns.find((campaign) => campaign._id === id)
    },
    getPromoById(id) {
      return this.promos.find((promo) => promo._id === id)
    },
    initialize() {
      this.status = this.FILTER_STATUSES.CHOOSE_ACTION
      if (this.listFilters && this.listFilters.actions) {
        this.currentActions = [...this.listFilters.actions]
      }
    },
    reset() {
      this.currentActions = []
      this.applyFilters()
      this.$refs.popperRef.doClose()
    },
    applyFilters() {
      // match with the current actions OR have a default action in allCurrentActions
      if (this.currentActions.length == 0) {
        this.allCurrentActions = []
        this.allCurrentActions.push(this.getDefaultAction())
      } else {
        this.allCurrentActions = this.currentActions
      }
      this.$emit('updateActions', this.currentActions)
    },
    getDefaultAction() {
      return {
        id: Math.random().toString(),
        did: true,
        eventType: this.ANALYTIC_EVENT_TYPES.CHOOSE_ACTION
      }
    },
    addNewAction() {
      this.currentActions.push({
        id: Math.random().toString(),
        did: true,
        eventType: this.ANALYTIC_EVENT_TYPES.LEFT_SURVEY
      })
      this.applyFilters()
    },
    updateAction(index, key, value) {
      this.selectedActionIndex = index
      const currentAction = { ...this.allCurrentActions[index] }
      switch (true) {
        case key === this.FILTER_KEYS.EVENT && value === this.ANALYTIC_EVENT_TYPES.SENT_CAMPAIGN:
          this.status = this.FILTER_STATUSES.CHOOSE_CAMPAIGN
          break
        case key === this.FILTER_KEYS.EVENT && value === this.ANALYTIC_EVENT_TYPES.REDEEM_PROMO:
          this.status = this.FILTER_STATUSES.CHOOSE_PROMO
          break
        case key === this.FILTER_KEYS.EVENT && value === this.ANALYTIC_EVENT_TYPES.COME_FROM:
          this.status = this.FILTER_STATUSES.CHOOSE_SOURCE
          break
        case key === this.FILTER_KEYS.CAMPAIGN:
          delete currentAction[this.FILTER_KEYS.PROMO]
          break
        case key === this.FILTER_KEYS.PROMO:
          delete currentAction[this.FILTER_KEYS.CAMPAIGN]
          break
        default:
          delete currentAction[this.FILTER_KEYS.PROMO]
          delete currentAction[this.FILTER_KEYS.CAMPAIGN]
      }

      currentAction[key] = value
      this.currentActions.splice(index, 1, currentAction)
      this.applyFilters()
    },
    removeAction(index) {
      this.currentActions.splice(index, 1)
      this.applyFilters()
    },
    selectCampaign(campaignId) {
      if (typeof campaignId !== 'undefined') {
        this.updateAction(this.selectedActionIndex, this.FILTER_KEYS.CAMPAIGN, campaignId)
      }
      this.status = this.FILTER_STATUSES.CHOOSE_ACTION
    },
    selectSource(source) {
      if (typeof source !== 'undefined') {
        this.updateAction(this.selectedActionIndex, this.FILTER_KEYS.SOURCE, source)
      }
      this.status = this.FILTER_STATUSES.CHOOSE_ACTION
    },
    selectPromo(promoId) {
      if (typeof promoId !== 'undefined') {
        this.updateAction(this.selectedActionIndex, this.FILTER_KEYS.PROMO, promoId)
      }
      this.status = this.FILTER_STATUSES.CHOOSE_ACTION
    },
    isCurrentStatus(status) {
      return this.status === status
    },
    getAnalyticEventType(type) {
      return this.ANALYTIC_EVENTS.find((eventType) => eventType.type === type)
    }
  },
  async mounted() {
    this.allCurrentActions.push(this.getDefaultAction())
    await this.queryCampaigns()
    await (this.campaignSource ? this.queryPromosWithSource() : this.queryPromos())
    this.initialize()
  }
}
</script>
